// CRM pipelines: the shared vocabulary the board, the card tile and the drawer read
// from — kept out of the components so the column styling and the pinned rule live in
// exactly one place.
import type { PipelineCard, PipelineStage, StageKind, TransitionWhy } from '@api';
import { fmtPrice, fullDate, parseTs } from '@ui/member';

/** Column styling by state kind. Nothing is terminal under the state model — `won` and
 *  `lost` are simply the outcome you want and the one you don't, and a card can leave
 *  either the moment the member's signals change. */
export const STAGE_KIND_META: Record<StageKind, { dot: string; count: string; ring: string }> = {
  in_progress: { dot: 'bg-primary', count: 'bg-primary/12 text-primary', ring: 'ring-base-content/8' },
  won: { dot: 'bg-success', count: 'bg-success/15 text-success', ring: 'ring-success/25' },
  // `lost` is Departed / Churned — the theme's error red, matching the membership
  // statuses and the departed contributor role, so the same fact is the same colour
  // whichever surface shows it.
  lost: { dot: 'bg-error', count: 'bg-error/12 text-error', ring: 'ring-error/25' },
};

/** A one-line description of what a pipeline tracks, for the switcher/header. Keyed
 *  by the pipeline's own kind; an unknown (custom) kind falls back to a generic line. */
export const PIPELINE_KIND_META: Record<string, { blurb: string }> = {
  // Deliberately not "new members": this pipeline holds EVERY member, and most of
  // them have been around for years. It grades contribution, not tenure.
  newcomer: { blurb: 'Where every member sits between silent and contributing.' },
  engagement: { blurb: 'Win back proven contributors who are slipping away — most recoverable first.' },
  // `churn` is retired — Subscription Churn merged into Membership, which now carries
  // the whole paying relationship: who is on which plan, who has cancelled, who is lost.
  membership: { blurb: 'The paying relationship — who is on which plan, who cancelled, who churned.' },
  // A board the owner authored. Deliberately says what is DIFFERENT about it rather
  // than what it tracks — only they know that, and the one thing they cannot infer from
  // looking at it is that nothing here moves on its own.
  custom: { blurb: 'Your own board — members appear here only when you add them.' },
};

export function pipelineBlurb(kind: string): string {
  return PIPELINE_KIND_META[kind]?.blurb ?? 'Move members through this pipeline from first touch to outcome.';
}

/** A card the owner has moved by hand is pinned for 7 days — the engine skips its
 *  transition rules while pinned, so the correction sticks. True while `pinned_until`
 *  is still in the future. */
export function isPinned(card: PipelineCard, nowMs: number): boolean {
  if (!card.pinned_until) return false;
  const until = new Date(card.pinned_until).getTime();
  return Number.isFinite(until) && until > nowMs;
}

/** Title-case a snake/lower token for a chip label ("felt_unheard" → "Felt unheard"). */
export function humanize(s: string | null | undefined): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
}

/** The stage a card sits in, resolved against the pipeline's stage list (for the
 *  drawer header / move menu — the card carries only `stage_key`). */
export function stageOf(stages: PipelineStage[], stageKey: string): PipelineStage | null {
  return stages.find((st) => st.key === stageKey) ?? null;
}

// --- why the engine moved a card ----------------------------------------------------
//
// A system move records `{state, when, signals}` — the state that claimed the member,
// the predicate it matched, and what each signal in that predicate read for them at that
// moment. The predicate is a condition tree written for an evaluator and is not shown;
// the READINGS are, because they are the whole answer to "why this member, now".
//
// GENERIC ON PURPOSE. The signal registry grows, and a renderer that knew a fixed set of
// names would silently drop the very reading that explains a surprising move. Everything
// here works off the shape of the value, never off a list of names.

/** One signal and what this member read for it, ready to render. */
export interface WhyReading {
  name: string;
  label: string;
  value: string;
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T/;

/** A signal value as a person reads it. Booleans are the common case and `true` is not
 *  an answer to "was this member paying"; numbers are trimmed to two decimals because
 *  momentum arrives as a float with fifteen. */
export function signalValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return String(Number(value.toFixed(2)));
  if (ISO_INSTANT.test(value) && !Number.isNaN(parseTs(value).getTime())) {
    return fullDate(value);
  }
  return humanize(value);
}

/**
 * The readings behind one system move.
 *
 * A signal is named `namespace.leaf` (`contributor.role`, `activity.momentum`) and the
 * leaf alone reads best — until two namespaces share one, which is why a leaf that is
 * not unique within this move keeps its full path instead of appearing twice.
 */
export function whyReadings(why: TransitionWhy | null | undefined): WhyReading[] {
  const signals = why?.signals;
  if (!signals) return [];
  const names = Object.keys(signals).sort();
  const leaf = (n: string): string => n.slice(n.lastIndexOf('.') + 1);
  const seen = new Map<string, number>();
  for (const n of names) seen.set(leaf(n), (seen.get(leaf(n)) ?? 0) + 1);
  return names.map((name) => {
    const raw = signals[name];
    // MINOR UNITS ARE A STORAGE DETAIL. A signal whose name ends in `_cents` is money
    // everywhere else in this app, and `4900` beside "amount monthly" reads as a
    // number of dollars to everyone who has not read the schema.
    const money = name.endsWith('_cents') && typeof raw === 'number';
    const plain = money ? name.slice(0, -'_cents'.length) : name;
    return {
      name,
      label: humanize(
        (seen.get(leaf(name)) ?? 0) > 1 ? plain.replace(/\./g, ' ') : leaf(plain)),
      value: money ? fmtPrice(raw as number) : signalValue(raw),
    };
  });
}

